import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Ricerca-mentre-digiti condivisa (2026-07-28) — sostituisce il pattern
// "campo + bottone lente che apre un popover".
//
// Due semantiche, perche' i campi che la usano NON sono equivalenti (verificato
// sul codice esistente, non deciso qui):
//
//  - mode="query"  : il testo digitato e' SOLO una query di ricerca; il valore
//                    si imposta unicamente scegliendo un risultato. Uscendo
//                    senza scegliere si torna al valore precedente. E' il caso
//                    di CC (codice Casadei), che oggi e' in sola lettura e puo'
//                    valere solo un codice esistente in anagrafica.
//  - mode="free"   : il testo digitato E' il valore (commit on blur, come
//                    prima); la tendina e' un aiuto facoltativo. E' il caso di
//                    CF (codice fornitore), oggi liberamente digitabile.
//
// Debounce 300ms e minimo 2 caratteri: stessi valori dei popover che sostituisce.

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

export function Typeahead<T>({
  value,
  placeholder,
  mode,
  search,
  renderItem,
  itemKey,
  onPick,
  onCommitText,
  disabled,
  inputStyle,
  className,
  title,
}: {
  value: string;
  placeholder?: string;
  mode: "query" | "free";
  search: (q: string) => Promise<T[]>;
  renderItem: (item: T) => { primary: string; secondary?: string };
  itemKey: (item: T) => string;
  onPick: (item: T) => void;
  /** solo mode="free": commit del testo digitato (on blur) */
  onCommitText?: (text: string) => void;
  disabled?: boolean;
  inputStyle?: React.CSSProperties;
  className?: string;
  title?: string;
}) {
  const [draft, setDraft] = useState(value);
  const [results, setResults] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(-1);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const boxRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // La tendina va in un PORTAL su document.body con position:fixed: dentro la
  // tabella verrebbe TAGLIATA dal wrapper con overflow-x:auto (verificato dal
  // vivo: i risultati erano nel DOM ma invisibili). Qui si calcola dove
  // ancorarla rispetto all'input.
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  useEffect(() => {
    if (!open) { setPos(null); return; }
    const place = () => {
      const r = boxRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open]);

  // Il valore puo' cambiare da fuori (undo, reset, ricalcolo): riallinea la bozza.
  useEffect(() => { setDraft(value); }, [value]);

  useEffect(() => {
    clearTimeout(timer.current);
    const q = draft.trim();
    if (!open || q.length < MIN_CHARS || q === value) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { setResults(await search(q)); } catch { setResults([]); } finally { setLoading(false); }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [draft, open]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Chiusura su click fuori: senza questo la tendina resta aperta sopra le
  // altre righe della tabella.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !boxRef.current?.contains(target)
        && !dropdownRef.current?.contains(target)
      ) closeAndRevert();
    };
    const t = setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", onDoc); };
  }, [open, draft]);   // eslint-disable-line react-hooks/exhaustive-deps

  function closeAndRevert() {
    setOpen(false);
    setResults([]);
    setIdx(-1);
    // mode="query": il digitato era solo una ricerca, non un valore.
    if (mode === "query") setDraft(value);
  }

  function pick(item: T) {
    setOpen(false);
    setResults([]);
    setIdx(-1);
    if (mode === "query") setDraft(value);  // il valore lo aggiorna il chiamante
    onPick(item);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.preventDefault(); closeAndRevert(); return; }
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Enter" && idx >= 0) { e.preventDefault(); pick(results[idx]); }
  }

  const q = draft.trim();
  return (
    <div ref={boxRef} style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <input
        type="text"
        className={className}
        value={draft}
        placeholder={placeholder}
        title={title ?? (draft || undefined)}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setDraft(e.target.value); setOpen(true); setIdx(-1); }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // mode="free": il testo digitato e' il valore (comportamento storico).
          if (mode === "free" && draft !== value) onCommitText?.(draft);
        }}
        style={{ width: "100%", minWidth: 0, ...inputStyle }}
      />
      {open && q.length >= MIN_CHARS && pos && createPortal((
        <div ref={dropdownRef} style={{
          position: "fixed", top: pos.top, left: pos.left, zIndex: 1000,
          minWidth: Math.max(280, pos.width), maxWidth: 380, background: "var(--indaco-white)",
          border: "1px solid var(--indaco-gray-300)", borderRadius: 8,
          boxShadow: "var(--shadow-md)", maxHeight: 240, overflowY: "auto",
        }}>
          {loading && (
            <div style={{ padding: "8px 10px", fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)", display: "flex", gap: 6, alignItems: "center" }}>
              <span className="spinner" style={{ width: 12, height: 12 }} /> Ricerca…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div style={{ padding: "8px 10px", fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-600)" }}>
              Nessun risultato per "{q}"
            </div>
          )}
          {results.map((item, i) => {
            const r = renderItem(item);
            return (
              <button
                key={itemKey(item)}
                type="button"
                className="flow-dropdown-item"
                onMouseDown={(e) => e.preventDefault()}   // evita il blur prima del click
                onClick={() => pick(item)}
                style={i === idx ? { background: "var(--indaco-gray-100)" } : undefined}
              >
                <div style={{ fontSize: "var(--font-size-sm)", fontWeight: 500 }}>{r.primary}</div>
                {r.secondary && (
                  <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)" }}>{r.secondary}</div>
                )}
              </button>
            );
          })}
        </div>
      ), document.body)}
    </div>
  );
}
